import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Upload, Sparkles, ArrowRight, ShieldCheck, Zap, MapPin } from "lucide-react";
import { MEDIA } from "@/lib/config";

export const Hero = () => {
    const navigate = useNavigate();

    return (
        <section className="relative min-h-[100svh] overflow-hidden bg-[#0A0A0A]">
            {/* Background */}
            <div className="absolute inset-0">
                <img
                    src={MEDIA.hero}
                    alt="Performance Fahrzeug Analyse"
                    className="h-full w-full object-cover object-center opacity-70"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/40" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/60" />
                <div className="absolute inset-0 bg-grid opacity-40" />
            </div>

            <div className="relative mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-center px-4 pb-20 pt-28 sm:px-6 lg:px-8">
                <div className="grid items-center gap-12 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5"
                        >
                            <Sparkles className="h-3.5 w-3.5 text-primary" />
                            <span className="text-xs font-semibold tracking-wide text-white">
                                KI-gestützter Performance Advisor
                            </span>
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.1 }}
                            className="mt-6 font-heading text-4xl font-extrabold leading-[1.05] tracking-tighter text-white sm:text-5xl lg:text-7xl"
                        >
                            Mehr Leistung <br />
                            für dein <span className="text-gradient-red">Fahrzeug.</span>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.2 }}
                            className="mt-6 max-w-xl text-base leading-relaxed text-[#A1A1AA] sm:text-lg"
                        >
                            Lade deinen Zulassungsschein hoch und erhalte innerhalb weniger Sekunden eine
                            KI-gestützte Analyse inklusive realistischer Optimierungsmöglichkeiten.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.3 }}
                            className="mt-9 flex flex-col gap-3 sm:flex-row"
                        >
                            <button
                                data-testid="hero-upload-btn"
                                onClick={() => navigate("/analyse")}
                                className="group inline-flex items-center justify-center gap-2.5 rounded-md bg-primary px-7 py-4 text-base font-bold text-white red-glow hover:bg-[#FF3B30] transition-colors"
                            >
                                <Upload className="h-5 w-5" />
                                Zulassungsschein hochladen
                            </button>
                            <button
                                data-testid="hero-analyse-btn"
                                onClick={() => navigate("/analyse")}
                                className="group inline-flex items-center justify-center gap-2 rounded-md border border-border bg-surface-elevated px-7 py-4 text-base font-medium text-white hover:border-primary/50 transition-colors"
                            >
                                Kostenlos analysieren
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                            </button>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.7, delay: 0.45 }}
                            className="mt-10 flex flex-wrap gap-x-6 gap-y-3"
                        >
                            {[
                                { icon: Zap, label: "Analyse in Sekunden" },
                                { icon: ShieldCheck, label: "Seriös & transparent" },
                                { icon: MapPin, label: "Wien & Umgebung" },
                            ].map(({ icon: Icon, label }) => (
                                <div key={label} className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                                    <Icon className="h-4 w-4 text-primary" />
                                    {label}
                                </div>
                            ))}
                        </motion.div>
                    </div>

                    {/* Floating analysis card */}
                    <motion.div
                        initial={{ opacity: 0, y: 40, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="lg:col-span-5"
                    >
                        <div className="relative mx-auto max-w-sm animate-float">
                            <div className="glass rounded-2xl border border-white/10 p-5 shadow-2xl">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs uppercase tracking-[0.2em] text-primary">
                                        Live Analyse
                                    </span>
                                    <span className="flex items-center gap-1.5 text-xs text-success">
                                        <span className="h-1.5 w-1.5 rounded-full bg-success" /> KI aktiv
                                    </span>
                                </div>
                                <div className="mt-4 text-2xl font-bold text-white">BMW 320d G20</div>
                                <div className="text-sm text-[#A1A1AA]">Stage 1 Optimierung</div>

                                <div className="mt-5 space-y-3">
                                    <div>
                                        <div className="mb-1 flex justify-between text-xs text-[#A1A1AA]">
                                            <span>Leistung</span>
                                            <span className="text-white">190 → 230 PS</span>
                                        </div>
                                        <div className="h-2 overflow-hidden rounded-full bg-[#1A1A1A]">
                                            <div className="h-full w-[82%] rounded-full bg-gradient-to-r from-[#CC0000] to-[#FF3B30]" />
                                        </div>
                                    </div>
                                    <div>
                                        <div className="mb-1 flex justify-between text-xs text-[#A1A1AA]">
                                            <span>Drehmoment</span>
                                            <span className="text-white">400 → 500 Nm</span>
                                        </div>
                                        <div className="h-2 overflow-hidden rounded-full bg-[#1A1A1A]">
                                            <div className="h-full w-[88%] rounded-full bg-gradient-to-r from-[#CC0000] to-[#FF3B30]" />
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-5 grid grid-cols-3 gap-2 border-t border-white/10 pt-4 text-center">
                                    <div>
                                        <div className="text-lg font-bold text-success">+40</div>
                                        <div className="text-[10px] text-[#A1A1AA]">PS</div>
                                    </div>
                                    <div>
                                        <div className="text-lg font-bold text-success">+100</div>
                                        <div className="text-[10px] text-[#A1A1AA]">Nm</div>
                                    </div>
                                    <div>
                                        <div className="text-lg font-bold text-primary">+21%</div>
                                        <div className="text-[10px] text-[#A1A1AA]">Mehr</div>
                                    </div>
                                </div>
                            </div>
                            <div className="absolute -right-3 -top-3 h-24 w-24 rounded-full bg-primary/30 blur-3xl" />
                        </div>
                    </motion.div>
                </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
        </section>
    );
};

export default Hero;
